import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { fetchProductsByCategory } from "../Redux/slice/productSlice";
import { Button } from "antd";
import { BsSpeakerFill } from "react-icons/bs";

const SpeakerDeals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [showArrows, setShowArrows] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [imageLoading, setImageLoading] = useState({});
  const [disableRightArrow, setDisableRightArrow] = useState(false);
  const hardcodedCategoryId = "38f7245d-b5f4-408a-9607-c3e3cb410ae4"

  const { productsByCategory = {}, loading } = useSelector((state) => state.products);
  const autoScrollInterval = useRef(null);

  useEffect(() => {
    dispatch(fetchProductsByCategory(hardcodedCategoryId));
  }, [dispatch]);

  useEffect(() => {
    const updateArrows = () => {
      if (containerRef.current) {
        setShowArrows(containerRef.current.scrollWidth > containerRef.current.clientWidth);
      }
    };
    updateArrows();
    window.addEventListener("resize", updateArrows);
    return () => window.removeEventListener("resize", updateArrows);
  }, [productsByCategory]);

  useEffect(() => {
    startAutoScroll();
    return () => stopAutoScroll();
  });

  const startAutoScroll = () => {
    stopAutoScroll();
    autoScrollInterval.current = setInterval(() => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
  
      // If at the end, scroll to the beginning
      if (container.scrollLeft >= maxScrollLeft - 10) {
        container.scrollTo({ left: 0, behavior: "smooth" });
        setShowLeftArrow(false);
        setDisableRightArrow(false); // Reset disable state
      } else {
        scrollRight();
      }
    }, 3000);
  };
  

  const stopAutoScroll = () => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
    }
  };

  const scrollLeft = () => {
    containerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    if (!containerRef.current) return;
  
    const container = containerRef.current;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
  
    // If at end, and user clicked manually, disable further scrolling
    if (container.scrollLeft >= maxScrollLeft - 10) {
      setDisableRightArrow(true);
    } else {
      container.scrollBy({ left: 200, behavior: "smooth" });
      setShowLeftArrow(true);
    }
  };
  
  const handleImageLoad = (productId) => {
    setImageLoading((prev) => ({ ...prev, [productId]: false }));
  };

  const handleImageError = (productId) => {
    setImageLoading((prev) => ({ ...prev, [productId]: true }));
  };

  const formatPrice = (amount) =>
    amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getValidImageUrl = (imagePath) => {
    if (!imagePath) return "default-image-url";
    return imagePath.includes("\\")
      ? `https://smfteapi.salesmate.app/Media/Products_Images/${imagePath.split("\\").pop()}`
      : imagePath;
  };

  const sortedProducts = (productsByCategory[hardcodedCategoryId] || [])
    .slice()
    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
    .slice(0, 10); // Only show the first 10 products

  return (
    <div className="container mx-auto px-2 mt-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-2 bg-green-600 p-2 rounded-md">
      <h2 className="text-sm flex items-center text-white">
          <BsSpeakerFill className="mr-2"/> Audio Systems
        </h2>
    
        <button
          className="text-xs text-white hover:underline flex items-center"
          onClick={() => navigate("/speakers")}
        >
          View All <RightOutlined className="ml-1" />
        </button>
      </div>

      {/* Product Carousel */}
      <div className="relative">
        {showArrows && (
          <>
            {showLeftArrow && (
              <Button
                icon={<LeftOutlined />}
                shape="circle"
                className="hidden sm:block absolute top-1/2 -translate-y-1/2 left-2 z-10 bg-white text-gray-800 shadow-md hover:bg-gray-200 transition-all duration-300"
                onClick={() => {
                  scrollLeft();
                  startAutoScroll();
                }}
              />
            )}
          <Button
  icon={<RightOutlined />}
  shape="circle"
  className={`hidden sm:block absolute top-1/2 -translate-y-1/2 right-2 z-10 bg-white text-gray-800 shadow-md ${
    disableRightArrow ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"
  } transition-all duration-300`}
  onClick={() => {
    if (!disableRightArrow) {
      scrollRight();
      startAutoScroll();
    }
  }}
  disabled={disableRightArrow}
/>

          </>
        )}
        <div
          ref={containerRef}
          className="flex gap-4 overflow-x-auto no-scrollbar scrollbar-hide px-2"
          onScroll={() => {
            const container = containerRef.current;
            if (!container) return;
          
            // Show/hide left arrow
            setShowLeftArrow(container.scrollLeft > 0);
          
            // Disable right arrow at end
            const maxScrollLeft = container.scrollWidth - container.clientWidth;
            setDisableRightArrow(container.scrollLeft >= maxScrollLeft - 10);
          }}
          
        >
          {loading
            ? [...Array(6)].map((_, index) => (
                <div
                  key={index}
                  className="bg-white p-3 rounded-lg shadow-md min-w-[150px] md:min-w-[200px] lg:min-w-[250px] animate-pulse"
                >
                  <div className="w-32 md:w-24 lg:w-48 h-32 bg-gray-300 rounded-lg"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mt-2"></div>
                  <div className="h-3 bg-gray-300 rounded w-1/2 mt-1"></div>
                </div>
              ))
            : sortedProducts.map((item) => (
                <div
                  key={item.productID}
                  className="relative bg-white text-black p-3 rounded-lg shadow-md flex flex-col cursor-pointer transform transition-transform duration-200 hover:scale-105 min-w-[150px] md:min-w-[200px] lg:min-w-[250px]"
                  onClick={() => navigate(`/product/${item.productID}`)}
                >
                  {item.oldPrice > 0 && (
                    <div className="absolute top-2 left-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                      {`${Math.round(((item.oldPrice - item.price) / item.oldPrice) * 100)}% OFF`}
                    </div>
                  )}
                  <div className="h-32 md:h-40 lg:h-48 flex items-center justify-center mb-3">
                    {imageLoading[item.productID] ? (
                      <div className="w-32 md:w-24 lg:w-48 h-32 bg-gray-300 rounded-lg animate-pulse"></div>
                    ) : (
                      <img
                        src={getValidImageUrl(item.productImage)}
                        alt={item.productName}
                        className="w-32 md:w-24 lg:w-48 object-cover rounded-lg"
                        onLoad={() => handleImageLoad(item.productID)}
                        onError={() => handleImageError(item.productID)}
                      />
                    )}
                  </div>
                  <p className="text-xs line-clamp-2">{item.productName}</p>
                  <p className="text-red-600 text-xs">GH₵ {formatPrice(item.price)}</p>
                  {item.oldPrice > 0 && (
                    <p className="text-gray-500 text-xs line-through">
                      GH₵ {formatPrice(item.oldPrice)}
                    </p>
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
};

export default SpeakerDeals;
